import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { setAuthToken } from '../../services/api.js';

const Spinner = () => (
  <svg className="animate-spin w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

const PoliceLoginModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [view, setView] = useState('login'); // 'login' | 'register' | 'reg-success'

  // Login state
  const [policeId, setPoliceId] = useState('');
  const [policePassword, setPolicePassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Register state
  const [govtId, setGovtId] = useState('');
  const [phone, setPhone] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);

  const resetAll = () => {
    setView('login');
    setPoliceId(''); setPolicePassword(''); setLoginError(''); setLoginLoading(false); setShowPassword(false);
    setGovtId(''); setPhone(''); setRegEmail(''); setRegError(''); setRegLoading(false);
  };

  const handleClose = () => { resetAll(); onClose(); };

  const switchTab = (tab) => {
    setView(tab);
    setLoginError('');
    setRegError('');
  };

  const handleLogin = async () => {
    setLoginError('');
    if (!policeId.trim() || !policePassword.trim()) {
      setLoginError('Please enter both Govt ID and Password.');
      return;
    }
    setLoginLoading(true);
    try {
      const { data } = await api.post('/auth/police/login', {
        policeId: policeId.trim(),
        password: policePassword,
      });
      const { token, officer } = data.data;
      setAuthToken(token);
      localStorage.setItem('police_token', token);
      localStorage.setItem('police_user', officer.name || officer.policeId);
      setLoginLoading(false);
      handleClose();
      navigate('/police-osint');
    } catch (err) {
      setLoginLoading(false);
      setLoginError(err.response?.data?.message || 'Login failed. Please check your Govt ID and password.');
    }
  };

  const handleRegister = () => {
    setRegError('');
    if (!govtId.trim()) { setRegError('Govt ID / Username is required.'); return; }
    if (!/^\d{10}$/.test(phone.replace(/\s/g, ''))) {
      setRegError('Enter a valid 10-digit phone number.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail.trim())) {
      setRegError('Enter a valid email address.');
      return;
    }
    setRegLoading(true);
    setTimeout(() => {
      const existing = JSON.parse(localStorage.getItem('police_registrations') || '[]');
      if (existing.find((r) => r.govtId.toLowerCase() === govtId.trim().toLowerCase())) {
        setRegError('This Govt ID is already registered or pending approval.');
        setRegLoading(false);
        return;
      }
      const newReg = {
        id: Date.now(),
        govtId: govtId.trim(),
        phone: phone.trim(),
        email: regEmail.trim(),
        status: 'pending',
        createdAt: new Date().toISOString(),
      };
      localStorage.setItem('police_registrations', JSON.stringify([...existing, newReg]));
      setRegLoading(false);
      setView('reg-success');
    }, 1200);
  };

  const fieldClass = 'w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-black text-sm placeholder-gray-400 outline-none focus:border-blue-500/60 focus:bg-white focus:ring-2 focus:ring-blue-500/15 transition-all disabled:opacity-50 disabled:cursor-not-allowed';
  const btnPrimary = 'relative w-full py-3.5 rounded-xl text-sm font-black rajdhani tracking-widest transition-all overflow-hidden mt-1 bg-gradient-to-r from-blue-600 to-cyan-500 text-white hover:shadow-xl hover:shadow-blue-500/25 hover:scale-[1.015] active:scale-[0.99]';
  const btnPrimaryDisabled = 'relative w-full py-3.5 rounded-xl text-sm font-black rajdhani tracking-widest transition-all overflow-hidden mt-1 bg-blue-700/60 cursor-not-allowed text-white/60';

  return (
    <div
      aria-modal="true"
      role="dialog"
      aria-label="Police Portal"
      className={`fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-950/90 backdrop-blur-xl transition-all duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`relative w-full sm:max-w-md transition-all duration-300 ${isOpen ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-5 opacity-0'}`}>
        <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-blue-400/80 to-transparent" />

        <div className="relative bg-white border border-blue-500/20 rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl shadow-blue-950/20">
          <div className="absolute -top-10 -right-10 w-56 h-56 bg-blue-600/6 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
          <div className="h-[3px] w-full bg-gradient-to-r from-blue-700 via-cyan-400 to-blue-700" />

          <div className="p-7 sm:p-8 relative">
            {/* Close */}
            <button
              type="button"
              aria-label="Close modal"
              onClick={handleClose}
              className="absolute top-5 right-5 w-8 h-8 rounded-lg bg-black/5 hover:bg-black/10 border border-black/10 flex items-center justify-center text-gray-500 hover:text-black transition-all"
            >
              <i className="fa-solid fa-xmark text-sm" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative shrink-0">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <i className="fa-solid fa-shield-halved text-xl text-white" />
                </div>
                <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-400 rounded-full border-2 border-white flex items-center justify-center">
                  <i className="fa-solid fa-check text-[8px] text-black" />
                </span>
              </div>
              <div>
                <h2 className="text-xl font-black text-black rajdhani tracking-wide leading-tight">Police Secure Portal</h2>
                <p className="text-gray-500 text-xs flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block animate-pulse" />
                  Authorized Law Enforcement Access Only
                </p>
              </div>
            </div>

            {/* Tab switcher */}
            {view !== 'reg-success' && (
              <div className="flex bg-gray-100 border border-gray-200 rounded-xl p-1 mb-3">
                <button
                  type="button"
                  onClick={() => switchTab('login')}
                  className={`flex-1 py-2 text-xs font-bold rajdhani tracking-widest rounded-lg transition-all ${view === 'login' ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg' : 'text-gray-500 hover:text-gray-800'}`}
                >
                  <i className="fa-solid fa-right-to-bracket mr-1.5" /> LOGIN
                </button>
                <button
                  type="button"
                  onClick={() => switchTab('register')}
                  className={`flex-1 py-2 text-xs font-bold rajdhani tracking-widest rounded-lg transition-all ${view === 'register' ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg' : 'text-gray-500 hover:text-gray-800'}`}
                >
                  <i className="fa-solid fa-user-plus mr-1.5" /> REGISTER
                </button>
              </div>
            )}


            {/* ── LOGIN VIEW ── */}
            {view === 'login' && (
              <div className="space-y-4">
                <div>
                  <label className="block mb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">Govt ID / Username</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                      <i className="fa-solid fa-user-tie text-sm" />
                    </span>
                    <input
                      type="text"
                      value={policeId}
                      onChange={(e) => setPoliceId(e.target.value)}
                      placeholder="Enter your Govt ID"
                      disabled={loginLoading}
                      className={fieldClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">Password</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                      <i className="fa-solid fa-lock text-sm" />
                    </span>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={policePassword}
                      onChange={(e) => setPolicePassword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                      placeholder="Enter your password"
                      disabled={loginLoading}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-11 py-3 text-black text-sm placeholder-gray-400 outline-none focus:border-blue-500/60 focus:bg-white focus:ring-2 focus:ring-blue-500/15 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`} />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none hover:text-gray-800 transition-colors">
                    <input type="checkbox" className="w-3.5 h-3.5 rounded accent-blue-500 cursor-pointer" />
                    Remember this device
                  </label>
                  <button type="button" className="text-xs text-blue-600 hover:text-blue-800 transition-colors">Forgot password?</button>
                </div>

                {loginError && (
                  <div className="flex items-start gap-2.5 bg-red-500/10 border border-red-500/25 rounded-xl px-3.5 py-2.5">
                    <i className="fa-solid fa-triangle-exclamation text-red-400 text-xs mt-0.5 shrink-0" />
                    <p className="text-xs text-red-300 leading-relaxed">{loginError}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleLogin}
                  disabled={loginLoading}
                  className={loginLoading ? btnPrimaryDisabled : btnPrimary}
                >
                  {loginLoading ? (
                    <span className="flex items-center justify-center gap-2"><Spinner /> Authenticating…</span>
                  ) : (
                    <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-shield-halved" /> Secure Login</span>
                  )}
                </button>
              </div>
            )}

            {/* ── REGISTER VIEW ── */}
            {view === 'register' && (
              <div className="space-y-4">
                <div>
                  <label className="block mb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">Govt ID / Username</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                      <i className="fa-solid fa-id-badge text-sm" />
                    </span>
                    <input
                      type="text"
                      value={govtId}
                      onChange={(e) => setGovtId(e.target.value)}
                      placeholder="e.g. MH-CID-20459"
                      disabled={regLoading}
                      className={fieldClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">Phone Number</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                      <i className="fa-solid fa-phone text-sm" />
                    </span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="10-digit mobile number"
                      disabled={regLoading}
                      className={fieldClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-widest">Official Email</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                      <i className="fa-solid fa-envelope text-sm" />
                    </span>
                    <input
                      type="email"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      placeholder="official@police.gov.in"
                      disabled={regLoading}
                      className={fieldClass}
                    />
                  </div>
                </div>

                {regError && (
                  <div className="flex items-start gap-2.5 bg-red-500/10 border border-red-500/25 rounded-xl px-3.5 py-2.5">
                    <i className="fa-solid fa-triangle-exclamation text-red-400 text-xs mt-0.5 shrink-0" />
                    <p className="text-xs text-red-300 leading-relaxed">{regError}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleRegister}
                  disabled={regLoading}
                  className={regLoading ? btnPrimaryDisabled : btnPrimary}
                >
                  {regLoading ? (
                    <span className="flex items-center justify-center gap-2"><Spinner /> Submitting Request…</span>
                  ) : (
                    <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-paper-plane" /> Submit Registration Request</span>
                  )}
                </button>

                <div className="flex items-start gap-2.5 bg-amber-500/8 border border-amber-500/20 rounded-xl px-3.5 py-2.5">
                  <i className="fa-solid fa-circle-info text-amber-400 text-xs mt-0.5 shrink-0" />
                  <p className="text-[11px] text-amber-800 leading-relaxed">
                    Your request will be reviewed by the admin. Password will be sent to your email <strong>only after approval</strong>.
                  </p>
                </div>
              </div>
            )}

            {/* ── SUCCESS VIEW ── */}
            {view === 'reg-success' && (
              <div className="text-center py-2">
                <div className="w-20 h-20 rounded-full bg-emerald-500/15 border-2 border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
                  <i className="fa-solid fa-hourglass-half text-3xl text-emerald-400" />
                </div>
                <h3 className="text-xl font-black text-black rajdhani tracking-wide mb-2">Request Submitted!</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-1">
                  Registration request for{' '}
                  <span className="text-cyan-400 font-bold mono">{govtId}</span>{' '}
                  has been submitted.
                </p>
                <p className="text-gray-600 text-xs leading-relaxed mb-5">
                  Admin will review and approve your request. Once approved, a password setup link will be sent to{' '}
                  <span className="text-blue-400">{regEmail}</span>.
                </p>

                <div className="space-y-2 mb-6 text-left">
                  {['Request submitted & pending admin review', 'Admin approves from dashboard', 'Password email sent to your official email', 'Set your password & login'].map((step, i) => (
                    <div key={step} className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 ${i === 0 ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-400' : 'bg-gray-100 border border-gray-200 text-gray-500'}`}>
                        {i === 0 ? <i className="fa-solid fa-check" /> : i + 1}
                      </span>
                      <span className={`text-xs ${i === 0 ? 'text-emerald-400' : 'text-gray-500'}`}>{step}</span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => switchTab('login')}
                  className="w-full py-3 rounded-xl text-sm font-black rajdhani tracking-widest bg-gray-100 border border-gray-200 text-gray-600 hover:text-black hover:border-gray-300 transition-all"
                >
                  <i className="fa-solid fa-arrow-left mr-2" /> Back to Login
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default PoliceLoginModal;
